
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import BottomNav from '../../components/feature/BottomNav';

const FlightsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tripType, setTripType] = useState<'oneWay' | 'roundTrip'>('roundTrip');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [classType, setClassType] = useState('economy');

  const popularDestinations = [
    {
      city: 'دبي',
      country: 'الإمارات',
      price: '1,250 ريال',
      image: 'https://public.readdy.ai/ai/img_res/5af0aaadc92b4007ff0600ee32f71a06.jpg'
    },
    {
      city: 'القاهرة',
      country: 'مصر',
      price: '980 ريال',
      image: 'https://readdy.ai/api/search-image?query=Cairo%20pyramids%20and%20sphinx%2C%20ancient%20Egyptian%20monuments%2C%20golden%20hour%20lighting%2C%20professional%20travel%20photography%2C%20high%20quality%2C%20desert%20landscape%2C%20centered%20composition&width=300&height=200&seq=9&orientation=landscape'
    },
    {
      city: 'إسطنبول',
      country: 'تركيا',
      price: '1,150 ريال',
      image: 'https://readdy.ai/api/search-image?query=Istanbul%20Hagia%20Sophia%20and%20Blue%20Mosque%2C%20historic%20Ottoman%20architecture%2C%20beautiful%20city%20view%2C%20professional%20travel%20photography%2C%20high%20quality%2C%20warm%20lighting%2C%20centered%20composition&width=300&height=200&seq=10&orientation=landscape'
    },
    {
      city: 'لندن',
      country: 'بريطانيا',
      price: '2,200 ريال',
      image: 'https://readdy.ai/api/search-image?query=London%20Big%20Ben%20and%20Thames%20river%2C%20iconic%20British%20landmarks%2C%20cloudy%20sky%2C%20professional%20travel%20photography%2C%20high%20quality%2C%20classic%20architecture%2C%20centered%20composition&width=300&height=200&seq=11&orientation=landscape'
    }
  ];

  const airlines = [
    {
      name: 'الخطوط السعودية',
      logo: 'https://readdy.ai/api/search-image?query=icon%2C%20Saudi%20Airlines%20logo%20design%2C%20professional%20airline%20branding%2C%20clean%20minimalist%20design%2C%20high%20quality%20details%2C%20the%20icon%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20corporate%20identity&width=80&height=80&seq=16&orientation=squarish',
      rating: 4.5,
      routes: '120+ وجهة'
    },
    {
      name: 'طيران الإمارات',
      logo: 'https://readdy.ai/api/search-image?query=icon%2C%20Emirates%20Airlines%20logo%20design%2C%20professional%20airline%20branding%2C%20clean%20minimalist%20design%2C%20high%20quality%20details%2C%20the%20icon%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20corporate%20identity&width=80&height=80&seq=17&orientation=squarish',
      rating: 4.8,
      routes: '150+ وجهة'
    },
    {
      name: 'فلاي أديل',
      logo: 'https://readdy.ai/api/search-image?query=icon%2C%20Flyadeal%20airline%20logo%20design%2C%20professional%20airline%20branding%2C%20clean%20minimalist%20design%2C%20high%20quality%20details%2C%20the%20icon%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20corporate%20identity&width=80&height=80&seq=18&orientation=squarish',
      rating: 4.2,
      routes: '50+ وجهة'
    },
    {
      name: 'ناس',
      logo: 'https://readdy.ai/api/search-image?query=icon%2C%20Flynas%20airline%20logo%20design%2C%20professional%20airline%20branding%2C%20clean%20minimalist%20design%2C%20high%20quality%20details%2C%20the%20icon%20should%20take%20up%2070%25%20of%20the%20frame%2C%20isolated%20on%20white%20background%2C%20centered%20composition%2C%20corporate%20identity&width=80&height=80&seq=19&orientation=squarish',
      rating: 4.3,
      routes: '60+ وجهة'
    }
  ];

  const handleSearch = () => {
    navigate('/flights/results');
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 bg-white shadow-sm z-40 h-16">
        <div className="flex items-center justify-between h-full px-4">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center flex-shrink-0"
          >
            <i className="ri-arrow-right-line text-gray-600"></i>
          </button>
          <h1 className="text-lg font-bold text-gray-800 truncate mx-4">حجز الطيران</h1>
          <div className="w-10 flex-shrink-0"></div>
        </div>
      </div>

      <div className="pt-20 px-4">
        {/* Trip Type Selector */}
        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm">
          <div className="flex bg-gray-100 rounded-lg p-1 mb-4">
            <button
              onClick={() => setTripType('roundTrip')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                tripType === 'roundTrip'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600'
              }`}
            >
              ذهاب وعودة
            </button>
            <button
              onClick={() => setTripType('oneWay')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                tripType === 'oneWay'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600'
              }`}
            >
              ذهاب فقط
            </button>
          </div>

          {/* From/To Inputs */}
          <div className="space-y-4">
            <div className="relative">
              <Input
                label="من"
                placeholder="اختر مدينة المغادرة"
                value={from}
                onChange={setFrom}
                icon={<i className="ri-takeoff-line"></i>}
              />
            </div>

            <div className="flex justify-center">
              <button className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center">
                <i className="ri-arrow-up-down-line text-blue-600"></i>
              </button>
            </div>

            <div className="relative">
              <Input
                label="إلى"
                placeholder="اختر مدينة الوصول"
                value={to}
                onChange={setTo}
                icon={<i className="ri-flight-land-line"></i>}
              />
            </div>

            {/* Date Inputs */}
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="تاريخ المغادرة"
                type="date"
                value={departureDate}
                onChange={setDepartureDate}
                icon={<i className="ri-calendar-line"></i>}
              />
              {tripType === 'roundTrip' && (
                <Input
                  label="تاريخ العودة"
                  type="date"
                  value={returnDate}
                  onChange={setReturnDate}
                  icon={<i className="ri-calendar-line"></i>}
                />
              )}
            </div>

            {/* Passengers and Class */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  عدد الركاب
                </label>
                <div className="flex items-center bg-gray-50 rounded-lg">
                  <button
                    onClick={() => setPassengers(Math.max(1, passengers - 1))}
                    className="w-10 h-10 flex items-center justify-center text-gray-600"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="flex-1 text-center font-medium">{passengers}</span>
                  <button
                    onClick={() => setPassengers(passengers + 1)}
                    className="w-10 h-10 flex items-center justify-center text-gray-600"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  درجة السفر
                </label>
                <div className="relative">
                  <select
                    value={classType}
                    onChange={(e) => setClassType(e.target.value)}
                    className="w-full bg-gray-50 border-none rounded-lg px-3 py-2 text-sm appearance-none"
                  >
                    <option value="economy">اقتصادية</option>
                    <option value="business">رجال أعمال</option>
                    <option value="first">أولى</option>
                  </select>
                  <i className="ri-arrow-down-s-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                </div>
              </div>
            </div>

            <Button
              onClick={handleSearch}
              className="w-full"
              size="lg"
            >
              <i className="ri-search-line ml-2"></i>
              البحث عن الرحلات
            </Button>
          </div>
        </div>

        {/* Popular Destinations */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">وجهات شائعة</h2>
          <div className="grid grid-cols-2 gap-4">
            {popularDestinations.map((destination, index) => (
              <button
                key={index}
                onClick={() => {
                  setTo(destination.city);
                }}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="relative h-24 overflow-hidden">
                  <img
                    src={destination.image}
                    alt={destination.city}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-2 right-2 text-white">
                    <h3 className="font-bold text-sm">{destination.city}</h3>
                    <p className="text-xs opacity-90">{destination.country}</p>
                  </div>
                </div>
                <div className="p-3">
                  <p className="text-blue-600 font-bold text-sm">من {destination.price}</p>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Airlines */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">شركات الطيران</h2>
          <div className="grid grid-cols-2 gap-4">
            {airlines.map((airline, index) => (
              <div key={index} className="bg-white rounded-xl p-4 shadow-sm">
                <div className="flex items-center space-x-3 space-x-reverse mb-3">
                  <div className="w-12 h-12 overflow-hidden rounded-full flex items-center justify-center">
                    <img
                      src={airline.logo}
                      alt={airline.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-sm text-gray-800 truncate">{airline.name}</h3>
                    <div className="flex items-center space-x-1 space-x-reverse">
                      <i className="ri-star-fill text-yellow-400 text-xs"></i>
                      <span className="text-xs text-gray-600">{airline.rating}</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500">{airline.routes}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Services */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">خدمات الطيران</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-wifi-line text-blue-600"></i>
              </div>
              <p className="text-xs font-medium text-gray-800">واي فاي مجاني</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-restaurant-line text-green-600"></i>
              </div>
              <p className="text-xs font-medium text-gray-800">وجبات مجانية</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center shadow-sm">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <i className="ri-luggage-cart-line text-purple-600"></i>
              </div>
              <p className="text-xs font-medium text-gray-800">حقائب إضافية</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <button
            onClick={() => navigate('/flights/manage')}
            className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center space-x-3 space-x-reverse"
          >
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-plane-line text-white"></i>
            </div>
            <div className="text-right flex-1">
              <h3 className="font-bold text-blue-800 text-sm">إدارة الحجز</h3>
              <p className="text-xs text-blue-600">تعديل أو إلغاء</p>
            </div>
          </button>
          
          <button className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center space-x-3 space-x-reverse">
            <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-white"></i>
            </div>
            <div className="text-right flex-1">
              <h3 className="font-bold text-green-800 text-sm">حالة الرحلة</h3>
              <p className="text-xs text-green-600">تتبع الرحلات</p>
            </div>
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default FlightsPage;
